// src/components/modals/UpdateAppointmentModal.tsx
import React, { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    ModalCloseButton, Button, VStack, FormControl, FormLabel, Input, Select,
    Alert, AlertIcon, Badge, Box, Divider, Flex, HStack, Icon, Stack, Text,
} from '@chakra-ui/react';
import { CalendarClock, Clock3, Mail, Pencil, Phone, Scissors, UserRound } from 'lucide-react';
import {
    AppointmentServiceAssignment,
    FetchedAppointment,
    AppointmentUpdatePayload,
    getAppointmentClientName,
} from '../../api/appointmentApi';
import { AppointmentStatus } from '../../types/enums';

const appointmentStatuses = Object.values(AppointmentStatus);
const casablancaTimeZone = 'Africa/Casablanca';

const formatInCasablanca = (value: string, options: Intl.DateTimeFormatOptions): string =>
    new Intl.DateTimeFormat('fr-MA', { timeZone: casablancaTimeZone, ...options }).format(new Date(value));

const formatForCasablancaInput = (value: string): string => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: casablancaTimeZone,
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(value));
    const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${byType.year}-${byType.month}-${byType.day}T${byType.hour}:${byType.minute}`;
};

const casablancaInputToIso = (value: string): string => {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    const target = Date.UTC(year, month - 1, day, hour, minute);
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: casablancaTimeZone,
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
    let candidate = target;

    for (let attempt = 0; attempt < 2; attempt += 1) {
        const parts = Object.fromEntries(formatter.formatToParts(new Date(candidate)).map((part) => [part.type, part.value]));
        const observed = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
        candidate += target - observed;
    }
    return new Date(candidate).toISOString();
};

const statusMeta: Record<string, { label: string; colorScheme: string }> = {
    pending: { label: 'En attente', colorScheme: 'yellow' },
    confirmed: { label: 'Confirmé', colorScheme: 'green' },
    cancelled: { label: 'Annulé', colorScheme: 'red' },
    done: { label: 'Terminé', colorScheme: 'gray' },
};

const assignmentRows = (appointment: FetchedAppointment): AppointmentServiceAssignment[] =>
    [...(appointment.service_assignments || [])].sort((left, right) => left.sequence - right.sequence);

interface UpdateAppointmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (id: number, data: AppointmentUpdatePayload) => Promise<void>;
    appointment: FetchedAppointment | null;
}

const UpdateAppointmentModal: React.FC<UpdateAppointmentModalProps> = ({
    isOpen, onClose, onSubmit, appointment,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<AppointmentUpdatePayload>({});
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (appointment) {
            const formattedTime = formatForCasablancaInput(appointment.appointment_time);
            setFormData({
                appointment_time: formattedTime,
                status: appointment.status,
            });
            setError(null);
            setIsEditing(false);
        } else {
            setFormData({});
            setIsEditing(false);
        }
    }, [appointment]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!appointment) return;
        const originalTime = formatForCasablancaInput(appointment.appointment_time);
        const payload: AppointmentUpdatePayload = {};
        if (formData.appointment_time && formData.appointment_time !== originalTime) {
            payload.appointment_time = casablancaInputToIso(formData.appointment_time);
        }
        if (formData.status && formData.status !== appointment.status) {
            payload.status = formData.status;
        }
        if (Object.keys(payload).length === 0) {
            onClose();
            return;
        }
        setError(null);
        setIsSubmitting(true);
        try {
            await onSubmit(appointment.id, payload);
        } catch (apiError: any) {
            setError(apiError.response?.data?.detail || "Failed to update appointment.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentStatus = statusMeta[appointment?.status || 'pending'] || statusMeta.pending;
    const segments = appointment ? assignmentRows(appointment) : [];
    const totalMinutes = appointment?.services.reduce((total, service) => total + service.duration_minutes, 0) || 0;

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl" mx={4} overflow="hidden">
                <ModalHeader
                    borderBottomWidth="1px" borderColor="gray.100"
                    fontSize="lg" fontWeight="700" color="gray.900" letterSpacing="-0.025em"
                >
                    <HStack spacing={3}>
                        <Flex w={9} h={9} align="center" justify="center" bg="brand.50" borderRadius="lg">
                            <Icon as={CalendarClock} boxSize={4} color="brand.600" />
                        </Flex>
                        <Box>
                            <Text fontSize="lg" fontWeight="700">Rendez-vous #{appointment?.id}</Text>
                            <Text fontSize="xs" fontWeight="500" color="gray.500">Heure du Maroc</Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton borderRadius="full" _hover={{ bg: 'gray.100' }} />
                {appointment && isEditing ? (
                    <form onSubmit={handleSubmit}>
                        <ModalBody py={6}>
                            <VStack spacing={5} align="stretch">
                                {error && (
                                    <Alert status="error" borderRadius="lg" fontSize="sm">
                                        <AlertIcon /> {error}
                                    </Alert>
                                )}
                                <Alert status="info" borderRadius="lg" fontSize="sm" alignItems="flex-start">
                                    <AlertIcon mt="1" />
                                    <Text>Modifier l&apos;heure réattribue automatiquement les praticiens disponibles pour chaque service.</Text>
                                </Alert>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Date et heure</FormLabel>
                                    <Input
                                        name="appointment_time" type="datetime-local"
                                        value={formData.appointment_time || ''}
                                        onChange={handleChange}
                                        borderRadius="lg" bg="gray.50"
                                        _focus={{ bg: 'white', borderColor: 'brand.500' }}
                                        isDisabled={isSubmitting}
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Statut</FormLabel>
                                    <Select
                                        name="status" value={formData.status || ''}
                                        onChange={handleChange}
                                        borderRadius="lg" bg="gray.50"
                                        _focus={{ bg: 'white', borderColor: 'brand.500' }}
                                        isDisabled={isSubmitting}
                                    >
                                        {appointmentStatuses.map(statusValue => (
                                            <option key={statusValue} value={statusValue}>
                                                {statusMeta[statusValue]?.label || statusValue}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </VStack>
                        </ModalBody>
                        <ModalFooter borderTopWidth="1px" borderColor="gray.100" gap={3}>
                            <Button variant="outline" onClick={() => setIsEditing(false)} isDisabled={isSubmitting} borderRadius="lg" fontWeight="600">
                                Retour
                            </Button>
                            <Button type="submit" colorScheme="brand" isLoading={isSubmitting} loadingText="Enregistrement..." borderRadius="lg" fontWeight="600">
                                Enregistrer
                            </Button>
                        </ModalFooter>
                    </form>
                ) : appointment ? (
                    <>
                        <ModalBody py={6}>
                            <VStack spacing={5} align="stretch">
                                <Flex justify="space-between" align="flex-start" gap={4}>
                                    <Box minW={0}>
                                        <Text fontSize="xl" fontWeight="700" color="gray.900" noOfLines={1}>{getAppointmentClientName(appointment)}</Text>
                                        <HStack mt={1.5} spacing={2} color="gray.600" fontSize="sm"><Icon as={Mail} boxSize={3.5} /><Text noOfLines={1}>{appointment.client.email || 'Email non renseigné'}</Text></HStack>
                                        {appointment.client.phone_number && <HStack mt={1} spacing={2} color="gray.600" fontSize="sm"><Icon as={Phone} boxSize={3.5} /><Text>{appointment.client.phone_number}</Text></HStack>}
                                    </Box>
                                    <Badge colorScheme={currentStatus.colorScheme} borderRadius="full" px={3} py={1} flexShrink={0}>{currentStatus.label}</Badge>
                                </Flex>

                                <Box borderWidth="1px" borderColor="brand.100" bg="brand.50" borderRadius="lg" p={4}>
                                    <HStack spacing={2} color="brand.800"><Icon as={Clock3} boxSize={4} /><Text fontSize="sm" fontWeight="700">{formatInCasablanca(appointment.appointment_time, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</Text></HStack>
                                    <Text mt={1} pl={6} fontSize="xs" color="brand.700">{totalMinutes} min au total</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3}>Déroulé du rendez-vous</Text>
                                    <Stack spacing={0} borderWidth="1px" borderColor="gray.100" borderRadius="lg" overflow="hidden">
                                        {segments.length > 0 ? segments.map((segment, index) => (
                                            <Flex key={`${segment.sequence}-${segment.service.id}`} p={3.5} gap={3} align="center" borderTopWidth={index === 0 ? 0 : '1px'} borderColor="gray.100">
                                                <Flex w={8} h={8} borderRadius="full" bg="gray.100" align="center" justify="center" flexShrink={0}><Text fontSize="xs" fontWeight="700" color="gray.600">{segment.sequence + 1}</Text></Flex>
                                                <Box flex="1" minW={0}><Text fontSize="sm" fontWeight="700" color="gray.800">{segment.service.name}</Text><HStack mt={0.5} color="gray.500" fontSize="xs"><Icon as={UserRound} boxSize={3} /><Text>{segment.staff.name}</Text></HStack></Box>
                                                <Text fontSize="xs" fontWeight="700" color="gray.600" textAlign="right">{formatInCasablanca(segment.starts_at, { hour: '2-digit', minute: '2-digit', hour12: false })}<br />{formatInCasablanca(segment.ends_at, { hour: '2-digit', minute: '2-digit', hour12: false })}</Text>
                                            </Flex>
                                        )) : appointment.services.map((service, index) => (
                                            <Flex key={service.id} p={3.5} gap={3} align="center" borderTopWidth={index === 0 ? 0 : '1px'} borderColor="gray.100"><Icon as={Scissors} color="gray.400" boxSize={4} /><Box><Text fontSize="sm" fontWeight="700" color="gray.800">{service.name}</Text><Text fontSize="xs" color="gray.500">{service.duration_minutes} min</Text></Box></Flex>
                                        ))}
                                    </Stack>
                                </Box>
                            </VStack>
                        </ModalBody>
                        <ModalFooter borderTopWidth="1px" borderColor="gray.100" gap={3}>
                            <Button variant="outline" onClick={onClose}>Fermer</Button>
                            <Button colorScheme="brand" leftIcon={<Pencil size={16} />} onClick={() => setIsEditing(true)}>Modifier</Button>
                        </ModalFooter>
                    </>
                ) : null}
            </ModalContent>
        </Modal>
    );
};

export default UpdateAppointmentModal;
